import React, {useCallback, useEffect, useState} from 'react';
import {View, ScrollView, StyleSheet, RefreshControl, Alert, Image, TouchableOpacity, Linking, StatusBar} from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import {
  Text,
  Button,
  ActivityIndicator,
  Dialog,
  Portal,
} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation, useRoute} from '@react-navigation/native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {useCustomerStore} from '../../store/useCustomerStore';
import {useMeasurementStore} from '../../store/useMeasurementStore';
import {colors, spacing} from '../../theme';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';

const DetailRow = ({label, value, icon}) => {
  if (!value) return null;
  return (
    <View style={styles.detailRow}>
      <View style={styles.detailLabelContainer}>
        {icon && <Icon name={icon} size={16} color={colors.textSecondary} style={{marginRight: 4}} />}
        <Text style={styles.detailLabel}>{label}</Text>
      </View>
      <Text style={styles.detailValue} numberOfLines={2}>{value}</Text>
    </View>
  );
};

const CustomerDetailScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const route = useRoute();
  const insets = useSafeAreaInsets();
  const {customerId} = route.params;

  const {currentCustomer, isLoading, fetchCustomerDetail, deleteCustomer, clearCurrentCustomer,
    customerStats, fetchCustomerStats, clearCustomerStats} = useCustomerStore();
  const {
    customerProfiles, bodyProfile,
    fetchCustomerProfiles, fetchBodyProfile,
    clearCustomerProfiles, clearBodyProfile,
    deleteProfile,
  } = useMeasurementStore();

  const [refreshing, setRefreshing] = useState(false);
  const [deleteVisible, setDeleteVisible] = useState(false);

  useEffect(() => {
    fetchCustomerDetail(customerId);
    fetchCustomerProfiles(customerId);
    fetchBodyProfile(customerId);
    fetchCustomerStats(customerId);
    return () => {
      clearCurrentCustomer();
      clearCustomerProfiles();
      clearBodyProfile();
      clearCustomerStats();
    };
  }, [customerId, fetchCustomerDetail, fetchCustomerProfiles, fetchBodyProfile, fetchCustomerStats, clearCurrentCustomer, clearCustomerProfiles, clearBodyProfile, clearCustomerStats]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        fetchCustomerDetail(customerId),
        fetchCustomerProfiles(customerId),
        fetchBodyProfile(customerId),
        fetchCustomerStats(customerId),
      ]);
    } finally {
      setRefreshing(false);
    }
  }, [customerId, fetchCustomerDetail, fetchCustomerProfiles, fetchBodyProfile, fetchCustomerStats]);

  const handleDelete = useCallback(async () => {
    setDeleteVisible(false);
    try {
      await deleteCustomer(customerId);
      navigation.goBack();
    } catch (e) {
      Alert.alert(t('common.error'), e.message);
    }
  }, [customerId, deleteCustomer, navigation, t]);

  const handleDeleteProfile = useCallback((profileId, profileName) => {
    Alert.alert(
      t('common.delete'),
      `${t('customer.confirmDelete').replace('customer', '"' + profileName + '"')}`,
      [
        {text: t('common.cancel'), style: 'cancel'},
        {
          text: t('common.delete'),
          style: 'destructive',
          onPress: async () => {
            try {
              await deleteProfile(customerId, profileId);
            } catch (e) {
              Alert.alert(t('common.error'), e.message);
            }
          },
        },
      ],
    );
  }, [customerId, deleteProfile, t]);

  if (isLoading && !currentCustomer) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  const customer = currentCustomer;

  return (
    <View style={styles.container}>
      <StatusBar barStyle="dark-content" backgroundColor="#A5D6A7" />
      <View style={{height: insets.top, backgroundColor: colors.primaryPastel}} />
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} colors={[colors.primary]} />
        }>
        {customer && (
          <>
            {/* Header + Avatar with full gradient */}
            <LinearGradient
              colors={[colors.primaryPastel, colors.surface]}
              locations={[0, 1]}
              style={styles.headerGradient}>
              {/* Appbar row */}
              <View style={styles.appbarRow}>
                <TouchableOpacity onPress={() => navigation.goBack()} style={styles.appbarBtn}>
                  <Icon name="arrow-left" size={24} color={colors.primary} />
                </TouchableOpacity>
                <Text style={styles.appbarTitle} numberOfLines={1}>{customer.fullName}</Text>
                <TouchableOpacity
                  onPress={() => navigation.navigate('CustomerForm', {customerId})}
                  style={styles.appbarBtn}>
                  <Icon name="pencil" size={22} color={colors.primary} />
                </TouchableOpacity>
              </View>
              {/* Avatar & Name */}
              <View style={styles.avatarContent}>
                <View style={styles.avatar}>
                  <Text style={styles.avatarText}>
                    {customer.fullName.charAt(0).toUpperCase()}
                  </Text>
                </View>
                <Text style={styles.name}>{customer.fullName}</Text>
                {customer.phone && (
                  <Text style={styles.phoneText}>{customer.countryCode || '+91'} {customer.phone}</Text>
                )}
                {/* Call / WhatsApp */}
                {customer.phone && (
                  <View style={styles.contactActions}>
                    <TouchableOpacity
                      activeOpacity={0.7}
                      onPress={() => Linking.openURL(`tel:${customer.countryCode || ''}${customer.phone}`)}
                      style={styles.contactBtn}>
                      <Icon name="phone" size={16} color={colors.primary} />
                      <Text style={styles.contactBtnText}>Call</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      activeOpacity={0.7}
                      onPress={() => {
                        const num = (customer.countryCode || '91') + customer.phone.replace(/\D/g, '');
                        Linking.openURL(`whatsapp://send?phone=${num}`);
                      }}
                      style={styles.whatsappBtn}>
                      <Icon name="whatsapp" size={16} color="#128C7E" />
                      <Text style={styles.whatsappBtnText}>WhatsApp</Text>
                    </TouchableOpacity>
                  </View>
                )}
              </View>
            </LinearGradient>

            {/* Stats */}
            {customerStats && (
              <View style={styles.statsGrid}>
                <TouchableOpacity
                  activeOpacity={0.7}
                  style={styles.statTile}
                  onPress={() => navigation.navigate('CustomerOrders', {
                    customerId,
                    customerName: customer.fullName,
                    summaryLabel: t('customer.stats.totalRevenue'),
                    summaryAmount: customerStats.totalRevenue || 0,
                    summaryCount: customerStats.orderCount || 0,
                  })}>
                  <Text style={styles.statsLabel}>{t('customer.stats.totalRevenue')}</Text>
                  <Text style={[styles.statsValue, {color: colors.primary}]}>
                    {'\u20B9'}{Number(customerStats.totalRevenue || 0).toLocaleString('en-IN')}
                  </Text>
                  <Text style={styles.statsSubtext}>
                    {t('customer.stats.orders', {count: customerStats.orderCount || 0})}
                  </Text>
                </TouchableOpacity>

                <TouchableOpacity
                  activeOpacity={0.7}
                  style={styles.statTile}
                  onPress={() => navigation.navigate('CustomerDues', {
                    customerId,
                    customerName: customer.fullName,
                  })}>
                  <Text style={styles.statsLabel}>{t('customer.stats.dueAmount')}</Text>
                  <Text style={[styles.statsValue, {color: customerStats.dueAmount > 0 ? colors.error : colors.success}]}>
                    {'\u20B9'}{Number(customerStats.dueAmount || 0).toLocaleString('en-IN')}
                  </Text>
                  <Text style={styles.statsSubtext}>
                    {t('customer.stats.dueOrders', {count: customerStats.dueOrderCount || 0})}
                  </Text>
                </TouchableOpacity>

                <TouchableOpacity
                  activeOpacity={0.7}
                  style={styles.statTile}
                  onPress={() => navigation.navigate('CustomerLedger', {
                    customerId,
                    customerName: customer.fullName,
                  })}>
                  <Text style={styles.statsLabel}>{t('customer.stats.totalPaid')}</Text>
                  <Text style={[styles.statsValue, {color: colors.success}]}>
                    {'\u20B9'}{Number(customerStats.totalPaid || 0).toLocaleString('en-IN')}
                  </Text>
                  {customerStats.walletBalance > 0 && (
                    <Text style={{fontSize: 11, color: colors.info, marginTop: 2}}>
                      Credits: {'\u20B9'}{Number(customerStats.walletBalance).toLocaleString('en-IN')}
                    </Text>
                  )}
                </TouchableOpacity>

                <TouchableOpacity
                  activeOpacity={0.7}
                  style={styles.statTile}
                  onPress={() => navigation.navigate('CustomerOrders', {
                    customerId,
                    customerName: customer.fullName,
                    summaryLabel: t('customer.stats.recentOrders'),
                    summaryAmount: customerStats.totalRevenue || 0,
                    summaryCount: customerStats.orderCount || 0,
                  })}>
                  <Text style={styles.statsLabel}>{t('customer.stats.recentOrders')}</Text>
                  {customerStats.recentOrders?.length > 0 ? (
                    customerStats.recentOrders.map(order => (
                      <View key={order.id} style={styles.recentOrderRow}>
                        <Text style={styles.recentOrderNumber}>{order.orderNumber}</Text>
                        <Text style={[styles.recentOrderStatus, {color: colors.status[order.status] || colors.textSecondary}]}>
                          {t(`order.status.${order.status}`)}
                        </Text>
                      </View>
                    ))
                  ) : (
                    <Text style={styles.statsSubtext}>{t('customer.stats.noOrders')}</Text>
                  )}
                </TouchableOpacity>
              </View>
            )}

            {/* Personal Details */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>{t('user.details')}</Text>
              <DetailRow icon="phone-plus-outline" label={t('customer.altPhone')} value={customer.altPhone} />
              <DetailRow icon="email-outline" label={t('customer.email')} value={customer.email} />
              <DetailRow icon="cake-variant-outline" label={t('customer.dateOfBirth')} value={customer.dateOfBirth} />
              <DetailRow icon="gender-male-female" label={t('customer.gender')} value={customer.gender === 'MALE' ? t('customer.male') : customer.gender === 'FEMALE' ? t('customer.female') : null} />
              <DetailRow icon="human-male-height" label={t('customer.height')} value={customer.heightCm ? `${customer.heightCm} cm` : null} />
              <DetailRow icon="weight" label={t('customer.weight')} value={customer.weightKg ? `${customer.weightKg} kg` : null} />
              <DetailRow icon="account-arrow-left-outline" label={t('customer.referralSource')} value={customer.referralSourceName} />
              <DetailRow icon="map-marker-outline" label={t('customer.address')} value={customer.address} />
              <DetailRow icon="city-variant-outline" label={t('customer.city')} value={customer.city} />
              <DetailRow icon="postage-stamp" label={t('customer.pincode')} value={customer.pincode} />
            </View>

            {/* Notes */}
            {customer.notes ? (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>{t('customer.notes')}</Text>
                <Text style={styles.notesText}>{customer.notes}</Text>
              </View>
            ) : null}

            {/* Customer Photos */}
            {(customer.faceImageUrl || customer.bodyImageUrl) && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>{t('customer.customerPhotos')}</Text>
                <View style={styles.imageRow}>
                  {customer.faceImageUrl && (
                    <View style={styles.imageContainer}>
                      <Image source={{uri: customer.faceImageUrl}} style={styles.customerImage} />
                      <Text style={styles.imageLabel}>{t('customer.faceImage')}</Text>
                    </View>
                  )}
                  {customer.bodyImageUrl && (
                    <View style={styles.imageContainer}>
                      <Image source={{uri: customer.bodyImageUrl}} style={styles.customerImage} />
                      <Text style={styles.imageLabel}>{t('customer.bodyImage')}</Text>
                    </View>
                  )}
                </View>
              </View>
            )}

            {/* Body Measurements */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>{t('measurement.bodyMeasurements')}</Text>
              {bodyProfile ? (
                <TouchableOpacity
                  activeOpacity={0.7}
                  style={styles.profileRow}
                  onPress={() => navigation.navigate('BodyMeasurement', {
                    customerId,
                    customerGender: customer.gender,
                  })}>
                  <View style={styles.profileIcon}>
                    <Icon name="human" size={20} color={colors.primary} />
                  </View>
                  <View style={{flex: 1}}>
                    <Text style={styles.profileName}>
                      {bodyProfile.profileName || t('measurement.bodyMeasurements')}
                    </Text>
                    <Text style={styles.profileMeta}>
                      {bodyProfile.measuredAt} &middot; {t('measurement.values', {count: bodyProfile.values?.length || 0})}
                    </Text>
                  </View>
                  <Icon name="chevron-right" size={22} color={colors.textSecondary} />
                </TouchableOpacity>
              ) : (
                <Button
                  mode="outlined"
                  icon="human"
                  onPress={() => navigation.navigate('BodyMeasurement', {
                    customerId,
                    customerGender: customer.gender,
                  })}
                  style={styles.actionButton}>
                  {t('measurement.addBodyMeasurement')}
                </Button>
              )}
            </View>

            {/* Dress Measurements */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>{t('measurement.dressMeasurements')}</Text>
              {customerProfiles.length > 0 && customerProfiles.filter(p => p.profileType === 'DRESS').map(profile => (
                <TouchableOpacity
                  key={profile.id}
                  activeOpacity={0.7}
                  style={styles.profileRow}
                  onPress={() => navigation.navigate('MeasurementForm', {
                    customerId,
                    profileId: profile.id,
                    catalogItemId: profile.catalogItemId,
                  })}>
                  {profile.catalogItemImageUrl ? (
                    <Image source={{uri: profile.catalogItemImageUrl}} style={styles.profileItemImage} />
                  ) : (
                    <View style={styles.profileIcon}>
                      <Icon name="ruler" size={20} color={colors.primary} />
                    </View>
                  )}
                  <View style={{flex: 1}}>
                    <Text style={styles.profileName}>{profile.profileName}</Text>
                    <Text style={styles.profileMeta}>
                      {profile.catalogItemName || ''}{profile.measuredAt ? ` \u00b7 ${profile.measuredAt}` : ''}{profile.values ? ` \u00b7 ${t('measurement.values', {count: profile.values.length})}` : ''}
                    </Text>
                  </View>
                  <TouchableOpacity
                    hitSlop={{top: 10, bottom: 10, left: 10, right: 10}}
                    onPress={() => handleDeleteProfile(profile.id, profile.profileName)}>
                    <Icon name="delete-outline" size={20} color={colors.error} />
                  </TouchableOpacity>
                </TouchableOpacity>
              ))}
              <Button
                mode="outlined"
                icon="ruler"
                onPress={() => navigation.navigate('MeasurementForm', {customerId})}
                style={[styles.actionButton, customerProfiles.filter(p => p.profileType === 'DRESS').length > 0 && {marginTop: spacing.sm}]}>
                {t('measurement.addDressMeasurement')}
              </Button>
            </View>

            {/* Delete */}
            <View style={styles.deleteSection}>
              <Button
                mode="outlined"
                icon="delete-outline"
                textColor={colors.error}
                style={styles.deleteButton}
                onPress={() => setDeleteVisible(true)}>
                {t('customer.deleteCustomer')}
              </Button>
            </View>
          </>
        )}
      </ScrollView>

      <Portal>
        <Dialog visible={deleteVisible} onDismiss={() => setDeleteVisible(false)} style={{borderRadius: 15}}>
          <Dialog.Title>{t('customer.deleteCustomer')}</Dialog.Title>
          <Dialog.Content>
            <Text>{t('customer.confirmDelete')}</Text>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setDeleteVisible(false)}>{t('common.cancel')}</Button>
            <Button textColor={colors.error} onPress={handleDelete}>{t('common.delete')}</Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  scrollContent: {paddingBottom: spacing.md},

  // Header gradient spanning appbar + avatar section
  headerGradient: {
    marginBottom: spacing.sm,
  },
  appbarRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.xs,
    height: 56,
  },
  appbarBtn: {
    width: 48,
    height: 48,
    justifyContent: 'center',
    alignItems: 'center',
  },
  appbarTitle: {
    flex: 1,
    color: colors.primary,
    fontSize: 18,
    fontWeight: '600',
    textAlign: 'center',
  },
  avatarContent: {
    alignItems: 'center',
    paddingTop: spacing.md,
    paddingBottom: spacing.xl,
  },
  avatar: {
    width: 72,
    height: 72,
    borderRadius: 36,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  avatarText: {color: colors.white, fontSize: 32, fontWeight: '700'},
  name: {fontSize: 20, fontWeight: '700', color: colors.text},
  phoneText: {fontSize: 14, color: colors.textSecondary, marginTop: 2},
  contactActions: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginTop: spacing.sm,
  },
  contactBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 6,
    paddingHorizontal: 14,
    borderRadius: 8,
    backgroundColor: colors.surface,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.primary + '40',
  },
  contactBtnText: {fontSize: 13, fontWeight: '600', color: colors.primary},
  whatsappBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 6,
    paddingHorizontal: 14,
    borderRadius: 8,
    backgroundColor: colors.surface,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: '#128C7E40',
  },
  whatsappBtnText: {fontSize: 13, fontWeight: '600', color: '#128C7E'},

  // Stats grid
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingHorizontal: spacing.md,
    paddingBottom: spacing.sm,
    gap: spacing.sm,
  },
  statTile: {
    width: '47%',
    flexGrow: 1,
    backgroundColor: colors.surface,
    borderRadius: 12,
    padding: spacing.md,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.border,
    elevation: 1,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 1},
    shadowOpacity: 0.06,
    shadowRadius: 3,
  },
  statsLabel: {fontSize: 11, color: colors.textSecondary, fontWeight: '500', marginBottom: 2},
  statsValue: {fontSize: 18, fontWeight: '800'},
  statsSubtext: {fontSize: 11, color: colors.textSecondary, marginTop: 2},
  recentOrderRow: {flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 3},
  recentOrderNumber: {fontSize: 12, color: colors.text},
  recentOrderStatus: {fontSize: 11, fontWeight: '600'},

  // Sections
  section: {
    backgroundColor: colors.surface,
    marginBottom: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: spacing.sm,
  },

  // Detail rows
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.divider,
  },
  detailLabelContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  detailLabel: {
    fontSize: 14,
    color: colors.textSecondary,
  },
  detailValue: {
    fontSize: 14,
    fontWeight: '500',
    color: colors.text,
    textAlign: 'right',
    flex: 1,
    marginLeft: spacing.md,
  },

  // Notes
  notesText: {
    fontSize: 14,
    color: colors.text,
    lineHeight: 20,
  },

  // Photos
  imageRow: {flexDirection: 'row', gap: spacing.md},
  imageContainer: {flex: 1, alignItems: 'center'},
  customerImage: {width: '100%', height: 150, borderRadius: 8, backgroundColor: colors.background},
  imageLabel: {fontSize: 12, color: colors.textSecondary, marginTop: spacing.xs, textAlign: 'center'},

  // Profile rows (body & dress measurements)
  profileRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.divider,
    gap: spacing.sm,
  },
  profileIcon: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: colors.primary + '12',
    justifyContent: 'center',
    alignItems: 'center',
  },
  profileItemImage: {width: 36, height: 36, borderRadius: 18, backgroundColor: colors.background},
  profileName: {fontSize: 14, fontWeight: '600', color: colors.text},
  profileMeta: {fontSize: 12, color: colors.textSecondary, marginTop: 1},
  actionButton: {borderColor: colors.primary, borderRadius: 8},

  // Delete
  deleteSection: {
    alignItems: 'center',
    paddingVertical: spacing.xl,
    paddingHorizontal: spacing.md,
  },
  deleteButton: {borderColor: colors.error, borderRadius: 8},
});

export default CustomerDetailScreen;
